// @flow
import * as React from 'react';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import Box from 'sail/Box';
import {Text, Intro} from 'sail/Text';
import {InputBase, TextInput} from 'sail/Input';
import {Button} from 'sail/Button';
import {InlineLink} from 'sail/InlineLink';
import Icon from 'sail/Icon';
import {useAccount, subscribeToEmailList} from 'docs/data';
import {Heading} from '../../core/Heading/Heading';

import './CollectEmail.scss';

type Props = {|
  children: React.Node,
  list: string,
  intro_text: React.Node,
  body_text: React.Node,
  form_cta_text?: React.Node,
  email_privacy_text: boolean,
  success_text?: React.Node,
  form_email_placeholder?: string,
  form_email_tag_name?: string,
  email_referrer_field?: React.Node,
  show_email_confirmation?: boolean,
  disable_campaign_monitor?: boolean,
|};

const messages = defineMessages({
  networkError: {
    id: 'docsShell.collectEmail.networkError',
    description: 'Error message to show when internet connection goes out',
    defaultMessage: 'You might have had an internet hiccup. Try again?',
  },
  privacyPolicy: {
    id: 'docsShell.collectEmail.privacyPolicy.md',
    description: 'Error message to show when internet connection goes out',
    defaultMessage:
      'You can unsubscribe at any time. Read our <a>privacy policy</a>.',
  },
  emailConfirmation: {
    id: 'docsShell.collectEmail.emailConfimation.md',
    description: 'Email confirmation message',
    defaultMessage: 'You are now subscribed!',
  },
  defaultFormEmailPlaceholder: {
    id: 'docsShell.collectEmail.defaultFormEmailPlaceholder',
    description: 'Placeholder for email input',
    defaultMessage: 'Email',
  },
  defaultFormCtaText: {
    id: 'docsShell.collectEmail.defaultFormCtaText',
    description: 'Text on button to subscribe to email updates',
    defaultMessage: 'Get updates',
  },
});

export const CollectEmail = ({
  list,
  intro_text,
  body_text,
  disable_campaign_monitor,
  form_cta_text,
  email_referrer_field,
  email_privacy_text = true,
  show_email_confirmation,
  success_text,
  form_email_tag_name,
  form_email_placeholder,
}: Props) => {
  const {account} = useAccount();
  const [pending, setPending] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [email, setEmail] = React.useState(
    account.user && account.user.email ? account.user.email : '',
  );

  const intl = useIntl();

  async function handleSubmit(e) {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      const response = await subscribeToEmailList(list, email);

      if (response.status > 204) {
        return setError('connection');
      }

      setSubmitted(true);
    } finally {
      setPending(false);
    }
  }

  return (
    <Box
      background="gray50"
      padding={{all: 20}}
      margin={{top: 48, bottom: 32}}
      style={{borderRadius: 5}}
    >
      <div
        className={`collect-email-partial ${submitted ? 'form-submitted' : ''}`}
      >
        <div className="intro-copy column">
          {(intro_text || body_text) && (
            <div className="collect-header">
              {intro_text && (
                <Box padding={{bottom: 24}}>
                  <Heading level={4}>
                    <Text size={24} weight="medium">
                      {intro_text}
                    </Text>
                  </Heading>
                </Box>
              )}
              {body_text && (
                <Box padding={{bottom: 12}}>
                  <Intro>{body_text}</Intro>
                </Box>
              )}
            </div>
          )}
          <form
            className="collect-form"
            data-disable-campaign-monitor={disable_campaign_monitor}
            onSubmit={handleSubmit}
          >
            <InputBase size="jumbo" width="maximized">
              <TextInput
                id="collect-email-input-email"
                type="email"
                width="maximized"
                className="email-field"
                required
                name={form_email_tag_name}
                placeholder={
                  form_email_placeholder ||
                  intl.formatMessage(messages.defaultFormEmailPlaceholder)
                }
                value={email}
                onChange={setEmail}
                pattern="[\s\S]+@[\s\S]+\.[\s\S]+$"
              />
              <input
                id="collect-email-input-list"
                type="hidden"
                name="list"
                value={list}
              />
              <Button
                id="collect-email-input-submit"
                color="white"
                label={
                  <Text color="blue">
                    {form_cta_text ||
                      intl.formatMessage(messages.defaultFormCtaText)}
                  </Text>
                }
                size="jumbo"
                ghost
                className="submit-button"
                type="submit"
                disabled={!email}
                pending={pending}
              />
            </InputBase>
            {email_referrer_field && (
              <>
                {/* Passes this page's URL to CM so that we know where we got this email from for future...
                Only works if you configure CM (on a list level) to expect the field and pass it as email_referrer_field */}
                <input
                  type="hidden"
                  name={email_referrer_field}
                  value={
                    typeof window !== 'undefined'
                      ? window.location.pathname
                      : ''
                  }
                />
              </>
            )}

            {error === 'connection' && (
              <p className="collect-error connection">
                <FormattedMessage {...messages.networkError} />
              </p>
            )}

            {email_privacy_text && (
              <div className="email-privacy-notice">
                <Text size={13} lineHeight={20} color="gray500">
                  <FormattedMessage
                    {...messages.privacyPolicy}
                    values={{
                      a: (label) => (
                        <InlineLink href="/privacy">{label}</InlineLink>
                      ),
                    }}
                  />
                </Text>
              </div>
            )}
          </form>
        </div>

        <div className="collect-success column">
          <Box padding={{bottom: 12}} flex={{justifyContent: 'center'}}>
            <Icon
              icon="checkCircle"
              color="green"
              size={40}
              margin={{bottom: 8}}
            />
          </Box>
          {show_email_confirmation && (
            <Intro>
              <FormattedMessage {...messages.emailConfirmation} />
              <br />
              {success_text && <Intro>{success_text}</Intro>}
            </Intro>
          )}
        </div>
      </div>
    </Box>
  );
};
